import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminAPI } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { useThemeStore } from '@/lib/stores';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Beaker, Users, BookTemplate, BarChart3,
  Sun, Moon, LogOut, LayoutDashboard, ChevronRight,
  Settings, ToggleLeft, UserPlus, MessageSquare, Mail, ClipboardList,
  Activity, KeyRound, Database, FolderKanban, Zap, AlertTriangle,
  TrendingUp, Menu, X
} from 'lucide-react';

const adminNav = [
  { path: '/admin', icon: LayoutDashboard, label: 'Overview', exact: true },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/admin/activity', icon: Activity, label: 'Activity Log' },
  { path: '/admin/feedback', icon: MessageSquare, label: 'Feedback' },
  { path: '/admin/email', icon: Mail, label: 'Email Center' },
  { path: '/admin/waitlist', icon: ClipboardList, label: 'Waitlist' },
  { path: '/admin/templates', icon: BookTemplate, label: 'Templates' },
  { path: '/admin/team', icon: UserPlus, label: 'Team' },
  { path: '/admin/config', icon: ToggleLeft, label: 'Platform Config' },
  { path: '/admin/account', icon: KeyRound, label: 'My Account' },
];

function SidebarContent({ location, toggleTheme, theme, logout, navigate, onNavClick }) {
  return (
    <>
      <div className="flex items-center gap-2.5 h-14 px-4 border-b border-border flex-shrink-0">
        <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
          <Beaker className="w-3.5 h-3.5 text-primary-foreground" />
        </div>
        <div>
          <span className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>Pharmax.ai</span>
          <span className="text-[10px] text-muted-foreground ml-1.5 font-mono">Admin</span>
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {adminNav.map(item => {
          const isActive = item.exact ? location.pathname === item.path : location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${
                isActive ? 'bg-primary/10 text-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <item.icon className={`w-4 h-4 ${isActive ? 'text-primary' : ''}`} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border space-y-1 flex-shrink-0">
        <Link to="/app" onClick={onNavClick} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-muted-foreground hover:text-foreground hover:bg-muted/50">
          <ChevronRight className="w-4 h-4" /> Back to App
        </Link>
        <Button variant="ghost" size="sm" onClick={toggleTheme} className="w-full justify-start gap-2.5 px-3 h-8 text-[13px]">
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="w-full justify-start gap-2.5 px-3 h-8 text-[13px] text-destructive">
          <LogOut className="w-4 h-4" /> Log Out
        </Button>
      </div>
    </>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuthStore();
  const { toggleTheme, theme } = useThemeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const sidebarProps = { location, toggleTheme, theme, logout, navigate, onNavClick: () => setMobileOpen(false) };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar — hidden on mobile */}
      <aside className="hidden md:flex w-[240px] border-r border-border flex-col h-screen sticky top-0">
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile Header + Sheet sidebar */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden flex items-center gap-3 h-14 px-4 border-b border-border sticky top-0 z-30 bg-background/95 backdrop-blur-sm">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="admin-mobile-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0 flex flex-col">
              <SidebarContent {...sidebarProps} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
              <Beaker className="w-3 h-3 text-primary-foreground" />
            </div>
            <span className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>Admin</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* ============== Dashboard (Overview) ============== */

const EVENT_ICONS = {
  auth: Users, project: FolderKanban, dataset: Database,
  chat: MessageSquare, error: AlertTriangle, admin: Settings, feedback: MessageSquare,
};
const EVENT_COLORS = {
  auth: 'text-blue-400', project: 'text-emerald-400', dataset: 'text-purple-400',
  chat: 'text-cyan-400', error: 'text-red-400', admin: 'text-amber-400', feedback: 'text-pink-400',
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const res = await adminAPI.enhancedStats();
      setStats(res.data);
    } catch (e) {
      try { const r2 = await adminAPI.stats(); setStats({ totals: r2.data, alerts: {}, today: {}, recent_activity: [] }); } catch {}
    } finally { setLoading(false); }
  };

  if (loading) return (
    <div className="p-4 sm:p-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
    </div>
  );

  const t = stats?.totals || {};
  const alerts = stats?.alerts || {};
  const today = stats?.today || {};
  const activity = stats?.recent_activity || [];

  const statCards = [
    { label: 'Total Users', value: t.users || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Projects', value: t.projects || 0, icon: FolderKanban, color: 'text-emerald-400' },
    { label: 'Datasets', value: t.datasets || 0, icon: Database, color: 'text-purple-400' },
    { label: 'Messages', value: t.messages || 0, icon: MessageSquare, color: 'text-cyan-400' },
    { label: 'New Users (7d)', value: alerts.new_users_7d || 0, icon: TrendingUp, color: 'text-green-400' },
    { label: 'Today Events', value: today.events || 0, icon: Zap, color: 'text-amber-400' },
    { label: 'Today Errors', value: today.errors || 0, icon: AlertTriangle, color: today.errors > 0 ? 'text-red-400' : 'text-muted-foreground' },
    { label: 'Pending Feedback', value: alerts.new_feedback || 0, icon: MessageSquare, color: alerts.new_feedback > 0 ? 'text-pink-400' : 'text-muted-foreground' },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Admin Overview</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Platform health and activity at a glance</p>
        </div>
        {(alerts.new_feedback > 0 || alerts.pending_waitlist > 0) && (
          <div className="flex gap-2 flex-wrap">
            {alerts.new_feedback > 0 && <Link to="/admin/feedback"><Badge variant="outline" className="border-pink-500/30 text-pink-400 text-[10px]">{alerts.new_feedback} new feedback</Badge></Link>}
            {alerts.pending_waitlist > 0 && <Link to="/admin/waitlist"><Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">{alerts.pending_waitlist} pending waitlist</Badge></Link>}
          </div>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
        {statCards.map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <Card className="h-full">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.color}`} />
                  <span className="text-xl sm:text-2xl font-bold font-mono" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>{s.value.toLocaleString()}</span>
                </div>
                <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-1">{s.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Activity Feed + Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="py-3 px-4"><CardTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>Recent Activity</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-4">
            {activity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No activity recorded yet.</p>
            ) : (
              <div className="space-y-0.5 max-h-[350px] overflow-y-auto">
                {activity.map((evt, i) => {
                  const Icon = EVENT_ICONS[evt.event_type] || Activity;
                  const color = EVENT_COLORS[evt.event_type] || 'text-muted-foreground';
                  return (
                    <div key={i} className="flex items-start gap-2 sm:gap-3 py-2 border-b border-border/30 last:border-0">
                      <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 ${color} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] sm:text-xs leading-relaxed">
                          <span className="font-medium">{evt.user_name || evt.user_email?.split('@')[0] || 'System'}</span>
                          <span className="text-muted-foreground"> {evt.event_name?.replace(/_/g, ' ')}</span>
                        </p>
                      </div>
                      <span className="text-[9px] sm:text-[10px] text-muted-foreground whitespace-nowrap">{formatTime(evt.timestamp)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <Link to="/admin/activity" className="text-xs text-primary hover:underline mt-2 inline-block">View all activity</Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-4"><CardTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>Quick Actions</CardTitle></CardHeader>
          <CardContent className="px-3 sm:px-4 pb-4 space-y-1">
            {[
              { to: '/admin/feedback', icon: MessageSquare, label: 'Review Feedback', color: 'text-pink-400', count: alerts.new_feedback },
              { to: '/admin/waitlist', icon: ClipboardList, label: 'Manage Waitlist', color: 'text-amber-400', count: alerts.pending_waitlist },
              { to: '/admin/email', icon: Mail, label: 'Send Email', color: 'text-blue-400' },
              { to: '/admin/analytics', icon: BarChart3, label: 'View Analytics', color: 'text-emerald-400' },
              { to: '/admin/users', icon: Users, label: 'Manage Users', color: 'text-cyan-400' },
              { to: '/admin/account', icon: KeyRound, label: 'Update My Password', color: 'text-purple-400' },
            ].map(item => (
              <Link key={item.to} to={item.to} className="flex items-center gap-2 p-2 sm:p-2.5 rounded-lg hover:bg-muted/50 text-xs sm:text-sm transition-colors">
                <item.icon className={`w-4 h-4 ${item.color}`} /> {item.label}
                {item.count > 0 && <Badge variant="outline" className="ml-auto text-[9px] border-primary/20">{item.count}</Badge>}
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
